import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui';
import { userApi, USER_ROLES, ROLE_LABELS } from '../../api/userApi';
import type { User, CreateUserData, UpdateUserData } from '../../api/userApi';
import { useToast } from '../../components/ui/Toast';

interface UserFormProps {
  user?: User | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface ValidationErrors {
  name?: string;
  email?: string;
  password?: string;
  role?: string;
}

const UserForm: React.FC<UserFormProps> = ({ user, onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: USER_ROLES.USER as string,
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const { addToast } = useToast();

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        role: user.role || USER_ROLES.USER,
      });
    }
  }, [user]);

  // Validation functions
  const validateName = (name: string): string | undefined => {
    if (!name.trim()) return 'Name is required';
    if (name.trim().length < 2) return 'Name must be at least 2 characters';
    if (name.trim().length > 100) return 'Name cannot exceed 100 characters';
    if (!/^[a-zA-Z\s'-]+$/.test(name.trim())) return 'Name can only contain letters, spaces, hyphens, and apostrophes';
    return undefined;
  };

  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) return 'Email is required';
    if (email.length > 255) return 'Email cannot exceed 255 characters';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!user && !password) return 'Password is required';
    if (password && password.length < 8) return 'Password must be at least 8 characters';
    if (password && !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(password)) {
      return 'Password must contain uppercase, lowercase, number, and special character';
    }
    return undefined;
  };

  const validateRole = (role: string): string | undefined => {
    if (!role) return 'Role is required';
    if (!Object.values(USER_ROLES).includes(role as any)) return 'Invalid role selected';
    return undefined;
  };

  const validateField = (name: string, value: string) => {
    let error: string | undefined;
    
    switch (name) {
      case 'name':
        error = validateName(value);
        break;
      case 'email':
        error = validateEmail(value);
        break;
      case 'password':
        error = validatePassword(value);
        break;
      case 'role':
        error = validateRole(value);
        break;
    }

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));

    return !error;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Validate field if it has been touched
    if (touched[name]) {
      validateField(name, value);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, value);
  };



  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    // Validate all fields
    const nameError = validateName(formData.name);
    const emailError = validateEmail(formData.email);
    const passwordError = validatePassword(formData.password);
    const roleError = validateRole(formData.role);

    if (nameError) {
      newErrors.name = nameError;
      isValid = false;
    }
    if (emailError) {
      newErrors.email = emailError;
      isValid = false;
    }
    if (passwordError) {
      newErrors.password = passwordError;
      isValid = false;
    }
    if (roleError) {
      newErrors.role = roleError;
      isValid = false;
    }

    setErrors(newErrors);
    
    // Mark all fields as touched
    setTouched({
      name: true,
      email: true,
      password: true,
      role: true,
    });

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      addToast({ 
        type: 'error', 
        title: 'Validation Error', 
        message: 'Please fix the errors below and try again' 
      });
      return;
    }

    setLoading(true);

    try {
      if (user) {
        // Update existing user
        const updateData: UpdateUserData = {
          name: formData.name.trim(),
          email: formData.email.trim(),
          role: formData.role,
        };

        await userApi.updateUser(user.id.toString(), updateData);
        addToast({ 
          type: 'success', 
          title: 'Success!', 
          message: `User "${formData.name}" has been updated successfully` 
        });
      } else {
        // Create new user
        const createData: CreateUserData = {
          name: formData.name.trim(),
          email: formData.email.trim(),
          password: formData.password,
          role: formData.role,
        };

        await userApi.createUser(createData);
        addToast({ 
          type: 'success', 
          title: 'Success!', 
          message: `User "${formData.name}" has been created successfully` 
        });
      }

      onSuccess();
    } catch (err: any) {
      // Handle server validation errors
      if (err.response?.data?.errors) {
        const serverErrors: ValidationErrors = {};
        err.response.data.errors.forEach((error: any) => {
          if (error.path) {
            serverErrors[error.path as keyof ValidationErrors] = error.msg;
          }
        });
        setErrors(serverErrors);
        addToast({ 
          type: 'error', 
          title: 'Validation Error', 
          message: 'Please fix the errors and try again' 
        });
      } else {
        addToast({ 
          type: 'error', 
          title: 'Error', 
          message: err.message || 'An unexpected error occurred' 
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name */}
        <div className="space-y-2">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Full Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors ${
              errors.name && touched.name
                ? 'border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20'
                : 'border-gray-300 focus:ring-blue-500 dark:border-gray-600'
            }`}
            placeholder="Enter full name"
          />
          {errors.name && touched.name && (
            <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.name}
            </p>
          )}
        </div>

        {/* Email */}
        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            onBlur={handleBlur}
            disabled={!!user}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors ${
              errors.email && touched.email
                ? 'border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20'
                : 'border-gray-300 focus:ring-blue-500 dark:border-gray-600'
            } ${!!user ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700' : ''}`}
            placeholder="Enter email address"
          />
          {errors.email && touched.email && (
            <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.email}
            </p>
          )}
          {!!user && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Email cannot be changed after user creation
            </p>
          )}
        </div>

        {/* Password */}
        {!user && (
          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Password *
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              onBlur={handleBlur}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors ${
                errors.password && touched.password
                  ? 'border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20'
                  : 'border-gray-300 focus:ring-blue-500 dark:border-gray-600'
              }`}
              placeholder="Enter secure password"
            />
            {errors.password && touched.password && (
              <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.password}
              </p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Must be 8+ characters with uppercase, lowercase, number, and special character
            </p>
          </div>
        )}

        {/* Role */}
        <div className="space-y-2">
          <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Role *
          </label>
          <select
            id="role"
            name="role"
            value={formData.role}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent dark:bg-gray-800 dark:text-white transition-colors ${
              errors.role && touched.role
                ? 'border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20'
                : 'border-gray-300 focus:ring-blue-500 dark:border-gray-600'
            }`}
          >
            <option value="">Select a role</option>
            {Object.entries(ROLE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          {errors.role && touched.role && (
            <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.role}
            </p>
          )}
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-600">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
          className="px-6 py-2"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white"
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {user ? 'Updating...' : 'Creating...'}
            </div>
          ) : (
            user ? 'Update User' : 'Create User'
          )}
        </Button>
      </div>
    </form>
  );
};

export default UserForm;